import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { IconPaletteFilled } from '@tabler/icons-react'

// Import theme CSS files so they're included in the build
import corporateBlueTheme from '@/themes/corporate-blue.css?url'
import modernSaasTheme from '@/themes/modern-saas.css?url'
import fintechGreenTheme from '@/themes/fintech-green.css?url'
import solanaWeb3Theme from '@/themes/solana-web3.css?url'
import bitcoinOrangeTheme from '@/themes/bitcoin-orange.css?url'
import cyberpunkNeonTheme from '@/themes/cyberpunk-neon.css?url'

export type Theme = 'default' | 'modern-saas' | 'fintech-green' | 'corporate-blue' | 'solana-web3' | 'bitcoin-orange' | 'cyberpunk-neon'

export interface ThemeConfig {
  id: Theme
  name: string
  description: string
  cssFile?: string
}

export const themes: ThemeConfig[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Neutral gray theme (shadcn/ui default)'
  },
  {
    id: 'corporate-blue',
    name: 'Corporate Blue',
    description: 'Professional blue theme for corporate environments',
    cssFile: corporateBlueTheme
  },
  {
    id: 'modern-saas',
    name: 'Modern SaaS',
    description: 'Purple-focused theme for modern applications',
    cssFile: modernSaasTheme
  },
  {
    id: 'fintech-green',
    name: 'Fintech Green',
    description: 'Emerald theme focused on growth and money',
    cssFile: fintechGreenTheme
  },
  {
    id: 'solana-web3',
    name: 'Solana Web3',
    description: 'Purple & green blockchain vibes inspired by Solana',
    cssFile: solanaWeb3Theme
  },
  {
    id: 'bitcoin-orange',
    name: 'Bitcoin Orange',
    description: 'Digital gold theme with warm orange tones',
    cssFile: bitcoinOrangeTheme
  },
  {
    id: 'cyberpunk-neon',
    name: 'Cyberpunk Neon',
    description: 'Futuristic neon pink and cyan theme',
    cssFile: cyberpunkNeonTheme
  }
]

export function ThemeSelector() {
  const [currentTheme, setCurrentTheme] = useState<Theme>('default')

  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme && themes.find(t => t.id === savedTheme)) {
      setCurrentTheme(savedTheme)
      applyTheme(savedTheme)
    }
  }, [])

  const applyTheme = async (themeId: Theme) => {
    // Remove existing theme stylesheets
    const existingThemeLinks = document.querySelectorAll('link[data-theme]')
    existingThemeLinks.forEach(link => link.remove())

    const theme = themes.find(t => t.id === themeId)
    if (!theme) return

    if (theme.cssFile) {
      // Load the theme CSS file
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = theme.cssFile
      link.setAttribute('data-theme', themeId)
      document.head.appendChild(link)
    }
    // Default theme doesn't need additional CSS as it's already in index.css
  }

  const selectTheme = (themeId: Theme) => {
    setCurrentTheme(themeId)
    applyTheme(themeId)
    localStorage.setItem('theme', themeId)
  }

  const currentThemeConfig = themes.find(t => t.id === currentTheme)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <IconPaletteFilled className="h-4 w-4" />
          {currentThemeConfig?.name}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Choose Theme</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {themes.map((theme) => (
          <DropdownMenuItem
            key={theme.id}
            onClick={() => selectTheme(theme.id)}
            className="flex flex-col items-start gap-1 p-3"
          >
            <div className="flex items-center gap-2">
              <div 
                className={`w-3 h-3 rounded-full border-2 ${
                  currentTheme === theme.id ? 'bg-primary border-primary' : 'border-muted-foreground'
                }`} 
              />
              <span className="font-medium">{theme.name}</span>
            </div>
            <span className="text-sm text-muted-foreground">{theme.description}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}